<template>
  <v-row no-gutters class="fill-height flex-column" style="overflow-y: auto; overflow-x: hidden;">
    <v-col cols="12">
      <card-title-row
        :straightTitle="$i18n.t(`sidebar.${netType}ModelingResults`)"
        class="py-6 pr-2"
        :has-buttons="isNetProfileButtonVisible"
      >
        <template #buttons>
          <base-rounded-button
            icon="mdi-chart-areaspline"
            size="x-small"
            @click="openNetProfile"
            :tooltip="$i18n.t('sidebar.sewerProfile.netProfile')"
          />
        </template>
      </card-title-row>
      <template v-if="isContentVisible">
        <template v-if="isScenarioAdded">
          <sidebar-row border-bottom class="mr-5 mb-5">
            <modeling-results-search :scenario-id="selectedScenario.id" :net-type="netType" />
          </sidebar-row>
          <v-row no-gutters class="px-3 pb-5">
            <v-col cols="auto">
              <div class="text-truncate flex-shrink-1 text-body-1 font-weight-medium">
                <v-icon size="22px">
                  mdi-hexagon-multiple
                </v-icon>
                {{ selectedModel.name }}
              </div>
            </v-col>
          </v-row>
          <sidebar-row>
            <v-row no-gutters class="pb-5 align-center">
              <v-col cols="auto">
                <div class="text-truncate flex-shrink-1 text-body-2 font-weight-medium">
                  {{ selectedScenario.name }}
                </div>
              </v-col>
              <v-col cols="auto" v-if="selectedScenarioQualityType !== 'NONE'">
                <div class="font-italic font-weight-light pl-1 pb-1" style="font-size: 12px">
                  {{ `(${selectedScenarioQualityType})` }}
                </div>
              </v-col>
              <v-spacer> </v-spacer>
              <v-col cols="auto" v-if="ekartExportEnabled && isWater">
                <v-icon size="22" @click="handleExportToEkart(scenarioId)" :disabled="ekartLoading">
                  mdi-export-variant
                </v-icon>
              </v-col>
              <v-col cols="auto">
                <dots-menu :items="dotsMenuItems" @delete="openDeleteScenarioDialog" @saveToLayer="saveResultToLayer" />
              </v-col>
            </v-row>
          </sidebar-row>
          <modeling-resulsts-layer
            v-for="layer in layers"
            :type="layer"
            :key="layer"
            :net-type="netType"
            :scenario-quality-type="selectedScenarioQualityTypeLowerCase"
          />
          <sidebar-row>
            <modeling-results-slider
              class="mr-5 py-6 mt-5 border--top"
              v-model="currentTimestepIndex"
              :timesteps="selectedScenario.timesteps"
            >
            </modeling-results-slider>
          </sidebar-row>
          <template v-if="isElementSelected">
            <template v-if="isElementFetched">
              <sidebar-row ref="element__title">
                <v-row no-gutters class="mr-5 py-5 border">
                  <v-col cols="auto">
                    <div class="text-truncate flex-shrink-1 text-body-1 font-weight-medium">
                      {{ elementCardTitle }}
                    </div>
                  </v-col>
                </v-row>
              </sidebar-row>
              <sidebar-row>
                <v-row no-gutters class="mr-5 pt-5">
                  <v-col cols="auto">
                    <v-btn text @click="openModelingChartDialog" class="pa-0" style="text-transform: unset !important">
                      <v-subheader class="text-body-2 pa-0">
                        <v-icon class="pr-2" size="22px">
                          mdi-chart-line
                        </v-icon>
                        {{ $i18n.t('sidebar.displayChart') }}
                      </v-subheader>
                    </v-btn>
                  </v-col>
                </v-row>
              </sidebar-row>
              <v-form>
                <feature-card-data :elements="computedAttribues" v-model="values" :isEditingOn="false">
                </feature-card-data>
              </v-form>
            </template>
            <main-loader v-else style="height: 500px"></main-loader>
          </template>
        </template>
        <template v-else>
          <v-row no-gutters class="align-baseline">
            <v-col cols="1">
              <v-icon color="rgba(0,0,0,0.7)" size="22px">
                mdi-upload
              </v-icon>
            </v-col>
            <v-col cols="10" class="border pb-4">
              <base-button-subheader
                v-if="!isScenarioAdded"
                @click="openLoadingDataDialog"
                translation-path="sidebar.loadModelingData"
              >
              </base-button-subheader>
            </v-col>
          </v-row>
        </template>
      </template>
      <main-loader v-else></main-loader>
    </v-col>
    <v-col>
      <main-dialog
        v-if="isScenarioDeleteDialogLoaded"
        :is-visible.sync="isScenarioDeleteDialogVisible"
        :title="$i18n.t('dialog.title.deleteScenario')"
        :body="$i18n.t('dialog.body.deleteScenario')"
        agree-text="dialog.agreeDelete"
        @agree="deleteScenario"
      />
      <load-modeling-data-dialog
        :is-visible.sync="isLoadModelingDataDialogVisible"
        v-if="isLoadModelingDataDialogLoaded"
        @addScenario="loadModelingData"
        :net-type="netType"
      ></load-modeling-data-dialog>
      <modeling-chart-dialog
        :is-visible.sync="isModelingChartDialogVisible"
        v-if="isModelingChartDialogLoaded"
        :scenario-id="scenarioId"
        :type="type"
        :element-label="elementLabel"
        :attributes="computedAttribues"
        :net-type="netType"
      >
      </modeling-chart-dialog>
    </v-col>
  </v-row>
</template>

<script>
import { get, sync, call } from 'vuex-pathify';

import { attributes } from '@/assets/js/modelingResults';
import { beforeModuleRouteEnter } from '@/assets/js/routerUtils';

import DotsMenu from '@/components/DotsMenu';
import CardTitleRow from '@/components/CardTitleRow';
import ModelingResultsSlider from '@/components/ModelingResultsSlider';
import MainLoader from '@/components/MainLoader';
import ModelingResulstsLayer from '@/components/ModelingResulstsLayer';
import FeatureCardData from '@/components/FeatureCardData';
import SidebarRow from '@/components/SidebarRow';
import ModelingResultsSearch from '@/components/ModelingResultsSearch';

export default {
  name: 'ModelingResults',
  components: {
    SidebarRow,
    DotsMenu,
    CardTitleRow,
    ModelingResultsSlider,
    MainLoader,
    ModelingResulstsLayer,
    FeatureCardData,
    ModelingResultsSearch,
    MainDialog: () => import('@/components/MainDialog'),
    LoadModelingDataDialog: () => import('@/components/LoadModelingDataDialog'),
    ModelingChartDialog: () => import('@/components/ModelingChartDialog'),
  },
  props: {
    scenarioId: {
      type: [Number, String],
      default: null,
    },
    type: {
      type: String,
      default: null,
    },
    elementId: {
      type: [Number, String],
      default: null,
    },
  },
  data: () => ({
    isScenarioDeleteDialogLoaded: false,
    isScenarioDeleteDialogVisible: false,
    isFetched: false,
    isElementFetched: false,
    isLoadModelingDataDialogVisible: false,
    isLoadModelingDataDialogLoaded: false,
    isModelingChartDialogVisible: false,
    isModelingChartDialogLoaded: false,
    currentTimeout: undefined,
    layers: ['nodes', 'links'],
    values: {},
    ekartLoading: false,
    exportScenarioToDataSourceLoading: false,
  }),
  computed: {
    currentTimestepIndex: sync('modeling/currentTimestepIndex'),
    isScenarioAddedStore: sync('modeling/isScenarioAdded'),
    isFromProfile: get('modeling/isFromProfile'),
    selectedModel: get('modeling/models@:netType@:selectedScenarioModel'),
    selectedScenario: get('modeling/modelsScenarios@:netType@:scenarioId'),
    element: get('modeling/scenariosElements@:netType@:scenarioId@:type@:elementId'),
    ekartExportEnabled: get('admin/settingsValues@water_modeling_ekart_export_enabled'),
    scenarioDataSourceExportEnabled: get('admin/settingsValues@water_modeling_datasource_export_enabled'),
    settings: get('admin/settingsValues'),
    netType() {
      return this.$route.params.netType;
    },
    isWater() {
      return this.netType === 'water';
    },
    isNetProfileButtonVisible() {
      return !this.isWater && this.isScenarioAdded;
    },
    computedAttribues() {
      return (this.currentAttribues || [])
        .filter(attribute => {
          const settingsEnabled = attribute.settingsNames
            ? attribute.settingsNames.every(setting => this.settings[setting])
            : true;
          const isCorrectQualityType = attribute.qualityName
            ? attribute.qualityName === this.selectedScenarioQualityTypeLowerCase
            : true;
          return settingsEnabled && isCorrectQualityType;
        })
        .map(attribute => {
          return {
            ...attribute,
            label: this.$i18n.t(`sidebar.${attribute.attribute}`),
          };
        });
    },
    currentAttribues() {
      return attributes[this.netType][this.type];
    },
    elementLabel() {
      return this.element?.label;
    },
    elementCardTitle() {
      return `${this.layerName}: ${this.elementLabel || `ID ${this.elementId}`}`;
    },
    layerName() {
      return this.type ? this.$i18n.t(`sidebar.${this.type}`) : '';
    },
    isScenarioAdded() {
      return this.scenarioId !== null && this.scenarioId !== undefined;
    },
    isElementSelected() {
      return this.elementId !== null && this.elementId !== undefined;
    },
    selectedScenarioModel() {
      return this.selectedScenario.model;
    },
    selectedScenarioQualityTypeValue() {
      return this.selectedScenario?.quality || 'NONE';
    },
    selectedScenarioQualityType() {
      return this.selectedScenarioQualityTypeValue.split('@')[0];
    },
    selectedScenarioQualityTypeLowerCase() {
      return this.selectedScenarioQualityType.toLocaleLowerCase();
    },
    computedTypeElementId() {
      return {
        type: this.type,
        elementId: this.elementId,
      };
    },
    isContentVisible() {
      return this.isFetched && ((this.isScenarioAdded && this.selectedScenario) || !this.isScenarioAdded);
    },
    dotsMenuItems() {
      return [
        {
          name: 'delete',
        },
        ...(this.isWater && this.scenarioDataSourceExportEnabled
          ? [{ name: 'saveToLayer', disabled: this.exportScenarioToDataSourceLoading }]
          : []),
      ];
    },

    layersData: get('layers/layers'),
    layersArray() {
      return this.$_objectToArray(this.layersData);
    },
    projectElements: get('layers/project@layers'),
    projectLayers() {
      return this.$_getFlatGroupsLayers(this.projectElements).layers;
    },
  },
  watch: {
    computedTypeElementId(nV, oV) {
      if (oV.elementId && oV.type) {
        this.resetHighlitedFeature(oV);
      }
      if (nV.elementId && nV.type) {
        this.getElement();
      }
    },
    scenarioId(nV, oV) {
      if (!oV && nV) {
        this.addModelingResultsLayers();
      } else {
        this.reset();
      }
    },
    currentTimestepIndex() {
      if (this.element) {
        this.setValues();
      }
    },
    isScenarioAdded(nV) {
      this.isScenarioAddedStore = nV;
    },
    netType() {
      this.reset();
      this.init();
    },
  },
  methods: {
    exportToEkart: call('modeling/exportToEkart'),
    exportScenarioToDataSource: call('modeling/exportScenarioToDataSource'),
    getModels: call('modeling/getModels'),
    getNetElementValues: call('modeling/getNetElementValues'),
    openNetProfile() {
      this.$router.push({ name: 'sewerProfile', params: this.$route.params });
    },
    getValue(name, valueQuery, isValueQueryEval) {
      let attribute;
      if (valueQuery) {
        const queryVars = [];
        const rxp = /{([^}]+)}/g;
        let curMatch;

        while ((curMatch = rxp.exec(valueQuery))) {
          queryVars.push(curMatch[1]);
        }
        for (const queryVar of queryVars) {
          let elementValue = this.element[queryVar];
          if (Array.isArray(elementValue)) {
            elementValue = elementValue[this.currentTimestepIndex];
          }
          valueQuery = valueQuery.replace(
            new RegExp('{' + queryVar + '}', 'gi'),
            elementValue || elementValue === 0 ? elementValue : ''
          );
        }
        attribute = isValueQueryEval ? eval(valueQuery) : valueQuery;
      } else {
        attribute = this.element[name];
      }
      if (Array.isArray(attribute)) {
        return attribute[this.currentTimestepIndex];
      }
      return attribute;
    },
    getValues(elements) {
      return elements.reduce((total, element) => {
        if (element.element_type === 'group') {
          return { ...total, ...this.getValues(element.elements) };
        }
        return {
          ...total,
          [element.attribute]: this.getValue(element.attribute, element.valueQuery, element.isValueQueryEval),
        };
      }, {});
    },
    setValues() {
      this.values = this.getValues(this.computedAttribues);
    },
    addModelingResultsLayers() {
      if (!this.selectedScenario) {
        return;
      }
      this.$root.$emit('addModelingResultsLayers', this.scenarioId, ['links', 'nodes']);
      this.$root.$emit(
        'fitView',
        this.selectedScenario.bbox.map(coord => +coord)
      );
      this.$root.$emit('modelingResultsIdentification-action', true);
    },
    async getElement() {
      try {
        this.isElementFetched = false;
        const { scenarioId, elementId, type, netType } = this;
        const r = await this.getNetElementValues({
          scenarioId,
          elementId,
          type,
          netType,
        });
        this.setValues();
        this.$store.set('map/ADD_SELECTED_FEATURES!', {
          type: 'highlitedFeatures',
          features: {
            [this.type]: [r.id],
          },
        });
        setTimeout(async () => {
          this.isElementFetched = true;
          await this.$nextTick();
          this.$refs.element__title.$el.scrollIntoView({ behavior: 'smooth' });
        }, 500);
      } catch (error) {
        console.log(error);
        this.isElementFetched = false;
      }
    },
    async init() {
      try {
        this.isFetched = false;
        await this.getModels(this.netType);
        if (this.selectedScenario) {
          this.addModelingResultsLayers();
        } else {
          this.$router.push({ name: 'modelingResults' });
        }
        setTimeout(() => {
          this.isFetched = true;
        }, 500);
        if (this.isElementSelected) {
          this.getElement();
        }
      } catch (error) {
        this.isFetched = false;
        console.log(error);
      }
    },
    openLoadingDataDialog() {
      this.isLoadModelingDataDialogVisible = true;
      this.isLoadModelingDataDialogLoaded = true;
    },
    openDeleteScenarioDialog() {
      this.isScenarioDeleteDialogVisible = true;
      this.isScenarioDeleteDialogLoaded = true;
    },
    openModelingChartDialog() {
      this.isModelingChartDialogVisible = true;
      this.isModelingChartDialogLoaded = true;
    },
    reset() {
      this.$root.$emit('removeModelingResultsLayers');
      this.$root.$emit('modelingResultsIdentification-action', false);
    },
    deleteScenario() {
      this.isScenarioDeleteDialogVisible = false;
      this.reset();
      this.$router.push({ name: 'modelingResults' });
    },
    loadModelingData(data) {
      this.isLoadModelingDataDialogVisible = false;
      this.selectedScenarioId = data;
    },
    resetHighlitedFeature({ type }) {
      this.$store.set('map/DELETE_SELECTED_FEATURES!', {
        type: 'highlitedFeatures',
        features: {
          [type]: [],
        },
      });
    },
    setLayerVisibility(layerId, visible) {
      this.$root.$emit('changeLayerVisibility', { layerId, visible });
    },
    async handleExportToEkart(scenarioId = this.scenarioId) {
      this.ekartLoading = true;
      try {
        const r = await this.exportToEkart(scenarioId);
        if (r.data.data) {
          this.$store.set('snackbar/PUSH_SUCCESSFULLY_MESSAGE!', {
            message: this.$i18n.t('success.successEkartExport'),
          });
        } else {
          this.$store.set('snackbar/PUSH_ERROR!', {
            message: this.$i18n.t('errors.errorEkartExport'),
          });
        }
      } finally {
        this.ekartLoading = false;
      }
    },
    async saveResultToLayer(scenarioId = this.scenarioId) {
      try {
        this.exportScenarioToDataSourceLoading = true;
        const r = await this.exportScenarioToDataSource({ scenarioId });
        this.$store.set('snackbar/PUSH_SUCCESSFULLY_MESSAGE!', {
          message: this.$i18n.t('success.successScenarioToDataSourceExport'),
        });
        const new_layers_ids = Object.values(r);
        const layersToProject = new_layers_ids.map(layerId => {
          return this.layersArray.find(layer => layer.id == layerId);
        });
        const layersNotInProject = layersToProject.filter(layer => {
          return !this.projectLayers.find(projectLayer => layer.id === projectLayer.id);
        });
        this.$root.$emit('addLayersHandler', layersNotInProject);
        setTimeout(() => {
          this.$store.set('snackbar/PUSH_MESSAGE!', {
            message: this.$i18n.t('snackbar.layersAddedToProject', {
              names: layersNotInProject.map(layer => `'${layer.name}'`).join(', '),
            }),
          });
        });
      } finally {
        this.exportScenarioToDataSourceLoading = false;
      }
    },
    onMounted() {
      this.reset();
      this.init();
    },
  },
  mounted() {
    if (!this.isFromProfile) {
      this.onMounted();
    } else {
      if (this.isElementSelected) {
        this.getElement();
      }
      this.isElementFetched = true;
      this.isFetched = true;
    }
  },
  beforeRouteEnter(to, from, next) {
    const config =
      to.params.netType === 'water'
        ? {
            additionalModulesNames: ['WATER_MODELING'],
            settingsNames: ['water_modeling_module_enabled'],
          }
        : {
            additionalModulesNames: ['SEWER_MODELING'],
            settingsNames: ['sewer_modeling_module_enabled'],
          };
    beforeModuleRouteEnter(to, from, next, { notPermittedNext: 'map', ...config });
  },
  beforeRouteLeave(to, from, next) {
    if (to.name !== 'sewerProfile') {
      if (this.isScenarioAdded) {
        this.reset();
        if (this.isElementSelected) {
          this.resetHighlitedFeature(this.computedTypeElementId);
        }
      }
    }
    next();
  },
};
</script>

<style lang="scss" scoped>
::v-deep {
  .v-btn--outlined {
    border-width: 2px;
  }
  .theme--light.v-label--is-disabled {
    color: rgba(0, 0, 0, 0.6);
  }
  .theme--light.v-input--is-disabled input,
  .theme--light.v-input--is-disabled textarea {
    color: rgba(0, 0, 0, 0.87);
  }
  .theme--light.v-text-field.v-input--is-disabled .v-input__slot::before {
    border-image: unset !important;
  }
}
.border--top {
  @include border(top);
}
.border {
  @include border(bottom);
}
</style>
